<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref } from 'vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import AnticipationUnavailable from 'dashboard/components-next/lais/AnticipationUnavailable.vue';
import VisitDateTimePickerDialog from './VisitDateTimePickerDialog.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  availabilityVisit: { type: Object, default: null },
  availabilityDays: { type: Array, default: () => [] },
  activeAvailabilityDay: { type: Object, default: null },
  selectedAvailabilityDay: { type: Number, default: 0 },
  isLoading: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
  errorMessage: { type: String, default: '' },
  showAnticipationUnavailable: { type: Boolean, default: false },
  anticipationUnavailableReason: { type: String, default: '' },
  loadingLabel: { type: String, default: 'Carregando' },
  errorTitle: { type: String, default: 'Não foi possível carregar' },
  retryLabel: { type: String, default: 'Tentar novamente' },
  highDemandLabel: { type: String, default: 'Alta procura' },
  datePickerLabel: { type: String, default: 'Selecione a data' },
  timePickerLabel: { type: String, default: 'Selecione o horário' },
  availabilityDateId: { type: Function, required: true },
  availabilityLabel: { type: Function, required: true },
  slotTimeId: { type: Function, required: true },
  slotHighDemand: { type: Function, required: true },
  availabilitySlotDisabled: { type: Function, required: true },
  availabilitySlotStatus: { type: Function, required: true },
});

const emit = defineEmits([
  'close',
  'goListing',
  'keepCurrent',
  'retry',
  'selectDay',
  'toggleSlot',
  'warningSelected',
  'save',
]);

const showDatePickerDialog = ref(false);
const showTimePickerDialog = ref(false);

const datePickerItems = computed(() =>
  props.availabilityDays.map((day, index) => ({
    key: props.availabilityDateId(day) || `day-${index}`,
    value: String(index),
    label: props.availabilityLabel(day),
    description: day?.description || day?.status || '',
    tag: day?.tag || '',
  }))
);

const activeSlots = computed(() => props.activeAvailabilityDay?.slots || []);

const selectedTimeSlot = computed(
  () => activeSlots.value.find(slot => slot.selected) || null
);

const selectedTimeValue = computed(() =>
  selectedTimeSlot.value ? props.slotTimeId(selectedTimeSlot.value) : ''
);

const selectedTimeLabel = computed(() => {
  const selectedSlots = activeSlots.value.filter(slot => slot.selected);
  if (!selectedSlots.length) return 'Escolha um horário';

  const firstSlot = props.slotTimeId(selectedSlots[0]);
  if (selectedSlots.length === 1) return firstSlot;

  return `${firstSlot} +${selectedSlots.length - 1}`;
});

const selectedDateLabel = computed(() => {
  const day = props.availabilityDays[props.selectedAvailabilityDay];
  return day ? props.availabilityLabel(day) : 'Escolha uma data';
});

const timePickerItems = computed(() =>
  activeSlots.value.map(slot => {
    const status = props.availabilitySlotStatus(slot);
    const highDemand = props.slotHighDemand(slot);
    return {
      key: props.slotTimeId(slot),
      value: props.slotTimeId(slot),
      label: props.slotTimeId(slot),
      description: status,
      tag: highDemand ? props.highDemandLabel : '',
      status: status || (highDemand ? 'high_demand' : ''),
      shortTimeStatus: highDemand,
      slot,
    };
  })
);

const selectDateFromDialog = item => {
  emit('selectDay', Number(item.value));
  showDatePickerDialog.value = false;
};

const selectTimeFromDialog = item => {
  if (item.shortTimeStatus) {
    emit('warningSelected', {
      warning: item.status || 'high_demand',
      time: item.value,
    });
  }
  emit('toggleSlot', item.slot);
  showTimePickerDialog.value = false;
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisModal
    :show="show"
    title="Horários para visita"
    max-width-class="max-w-[760px]"
    @close="emit('close')"
  >
    <div class="grid gap-5">
      <p class="text-[14px] leading-relaxed text-lais-text-body">
        Selecione abaixo os horários nos quais o seu imóvel pode receber
        visitas. Isso irá refletir no site da Imudei.
      </p>

      <div
        class="rounded-lais-sm border border-lais-gold/40 bg-lais-gold/10 px-4 py-3 text-[13px] leading-relaxed text-lais-text-body"
      >
        <p>
          Aumente as chances do seu imóvel receber visitas disponibilizando mais
          horários. Sem tempo para visitas?
        </p>
        <button
          type="button"
          class="mt-2 inline-flex items-center gap-1 text-[13px] font-semibold text-lais-primary underline decoration-lais-gold decoration-[1.5px] underline-offset-[3px]"
          @click="emit('goListing')"
        >
          Inative ou despublique o imóvel
          <span class="i-lucide-arrow-right size-3.5" aria-hidden="true" />
        </button>
      </div>

      <AnticipationUnavailable
        v-if="showAnticipationUnavailable"
        :visit="availabilityVisit"
        :reason="anticipationUnavailableReason"
        :error-message="errorMessage"
        @configure="emit('goListing')"
        @keep="emit('keepCurrent')"
        @retry="emit('retry')"
      />

      <div
        v-if="isLoading"
        class="grid min-h-[220px] place-items-center rounded-lais border border-lais-border bg-white text-[13px] text-lais-text-muted"
      >
        <span class="inline-flex items-center gap-2">
          <span
            class="i-lucide-loader-circle size-4 animate-spin"
            aria-hidden="true"
          />
          {{ loadingLabel }}
        </span>
      </div>

      <div
        v-else-if="errorMessage"
        class="grid min-h-[220px] place-items-center rounded-lais border border-lais-border bg-white px-4 text-center"
      >
        <div class="max-w-[360px]">
          <span
            class="i-lucide-circle-alert mx-auto mb-3 block size-8 text-lais-text-empty"
            aria-hidden="true"
          />
          <h3 class="text-[15px] font-semibold text-lais-text">
            {{ errorTitle }}
          </h3>
          <p class="mt-1 text-[13px] leading-relaxed text-lais-text-muted">
            {{ errorMessage }}
          </p>
          <button
            type="button"
            class="mt-4 inline-flex items-center gap-2 rounded-full border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary hover:bg-lais-primary-50"
            @click="emit('retry')"
          >
            <span class="i-lucide-refresh-cw size-3.5" aria-hidden="true" />
            {{ retryLabel }}
          </button>
        </div>
      </div>

      <div v-else class="grid gap-4">
        <div class="grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            class="flex min-h-12 items-center justify-between gap-3 rounded-lais border border-lais-border bg-white px-3 py-2 text-left transition-colors hover:border-lais-primary hover:text-lais-primary"
            @click="showDatePickerDialog = true"
          >
            <span class="min-w-0">
              <span
                class="block text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
              >
                {{ datePickerLabel }}
              </span>
              <span class="block truncate text-[13px] font-semibold">
                {{ selectedDateLabel }}
              </span>
            </span>
            <span
              class="i-lucide-calendar-days size-4 shrink-0"
              aria-hidden="true"
            />
          </button>

          <button
            type="button"
            class="flex min-h-12 items-center justify-between gap-3 rounded-lais border border-lais-border bg-white px-3 py-2 text-left transition-colors hover:border-lais-primary hover:text-lais-primary disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="!activeSlots.length"
            @click="showTimePickerDialog = true"
          >
            <span class="min-w-0">
              <span
                class="block text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
              >
                {{ timePickerLabel }}
              </span>
              <span class="block truncate text-[13px] font-semibold">
                {{ selectedTimeLabel }}
              </span>
            </span>
            <span class="i-lucide-clock-3 size-4 shrink-0" aria-hidden="true" />
          </button>
        </div>

        <div class="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
          <button
            v-for="(day, index) in availabilityDays"
            :key="availabilityDateId(day) || index"
            type="button"
            class="shrink-0 rounded-full border px-3 py-2 text-[12px] font-semibold transition-colors"
            :class="
              selectedAvailabilityDay === index
                ? 'border-lais-primary bg-lais-primary text-white'
                : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
            "
            @click="emit('selectDay', index)"
          >
            {{ availabilityLabel(day) }}
          </button>
        </div>

        <div
          v-if="activeAvailabilityDay"
          class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4"
        >
          <button
            v-for="slot in activeAvailabilityDay.slots"
            :key="slotTimeId(slot)"
            type="button"
            class="min-h-[72px] rounded-lais border px-3 py-2 text-left transition-colors"
            :class="[
              slot.selected
                ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary',
              availabilitySlotDisabled(slot)
                ? 'cursor-pointer border-orange-200 bg-orange-50 text-orange-800 hover:border-orange-300 hover:bg-orange-100'
                : '',
            ]"
            :aria-disabled="availabilitySlotDisabled(slot)"
            @click="emit('toggleSlot', slot)"
          >
            <span class="flex items-start justify-between gap-2">
              <span class="font-semibold">{{ slotTimeId(slot) }}</span>
              <span
                v-if="slot.selected"
                class="i-lucide-check size-4 shrink-0"
                aria-hidden="true"
              />
            </span>
            <span
              v-if="slotHighDemand(slot)"
              class="mt-1 inline-flex rounded-full bg-lais-gold/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-lais-text"
            >
              {{ highDemandLabel }}
            </span>
            <span
              v-if="availabilitySlotStatus(slot)"
              class="mt-1 block text-[11px] text-lais-text-muted"
            >
              {{ availabilitySlotStatus(slot) }}
            </span>
          </button>
        </div>

        <p
          v-if="activeAvailabilityDay && !activeAvailabilityDay.slots?.length"
          class="sr-only"
        >
          Sem horários disponíveis para este dia.
        </p>
        <AnticipationUnavailable
          v-if="activeAvailabilityDay && !activeAvailabilityDay.slots?.length"
          :visit="availabilityVisit"
          reason="no_slots"
          @configure="emit('goListing')"
          @keep="emit('close')"
          @retry="emit('retry')"
        />
      </div>
    </div>

    <VisitDateTimePickerDialog
      :show="showDatePickerDialog"
      :title="datePickerLabel"
      :items="datePickerItems"
      :selected-value="String(selectedAvailabilityDay)"
      empty-message="Nenhuma data disponível."
      @close="showDatePickerDialog = false"
      @select="selectDateFromDialog"
    />

    <VisitDateTimePickerDialog
      :show="showTimePickerDialog"
      :title="timePickerLabel"
      :items="timePickerItems"
      :selected-value="selectedTimeValue"
      empty-message="Nenhum horário disponível."
      @close="showTimePickerDialog = false"
      @select="selectTimeFromDialog"
    />

    <template #footer>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          Voltar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="primary"
          icon="i-lucide-check"
          :is-loading="isSaving"
          :disabled="isLoading || !!errorMessage"
          @click="emit('save')"
        >
          Confirmar horários
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
